import { Button, CircularProgress, IconButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { ApplicationActions } from '../../utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { projectsApi } from '../../api/projectsApi';
import { useAppSelector } from '../../redux/ReduxHooks';
import { projectSlice } from '../../redux/ProjectSlice';
import { Project } from './projects';
import { parseProjectFromApiResponse } from '../../utils/projectApiMetadata';

const ProjectsHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { OPEN_SHARE_CODE } = ApplicationActions.Dialogs;
  const location = useLocation();
  const initialTab = useMemo(() => {
    if (location.pathname.includes('/rapid-robots')) return 'rapid-robots';
    else return 'my-projects';
  }, [location.pathname]);

  const [value, setValue] = useState<string | null>(initialTab);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isRapidRobotsPage = useMemo(() => location.pathname.includes('/rapid-robots'), [location.pathname]);

  // Get all projects from Redux
  const projects = useAppSelector((state) => state.project.projects);

  useEffect(() => {
    if (value === 'my-projects') {
      navigate('/');
    } else if (value === 'rapid-robots') {
      navigate('/rapid-robots');
    }
  }, [value, navigate]);

  const handleOpenShareCode = () => {
    dispatch(applicationSlice.actions.onOpenDialog(OPEN_SHARE_CODE));
  };

  const handleRefreshSharedProjects = async () => {
    // Find all collaborative projects (those with shareCode)
    const sharedProjects = projects.filter((p) => p.shareCode);

    setIsRefreshing(true);

    try {
      // Refresh each shared project from the platform
      const refreshPromises = sharedProjects.map(async (localProject) => {
        try {
          if (!localProject.shareCode) return;

          // Fetch latest version from platform
          const response = await projectsApi.get(localProject.shareCode);

          const fetchedProject: Project = parseProjectFromApiResponse(response);

          // Merge with local metadata (keep pin, accessLevel, shareCode)
          const updatedProject: Project = {
            ...fetchedProject,
            shareCode: localProject.shareCode,
            pin: localProject.pin,
            accessLevel: localProject.accessLevel,
            readOnly: localProject.accessLevel === 'viewer',
          };

          // Update project in Redux
          dispatch(projectSlice.actions.onReplaceProject(updatedProject));
        } catch (error) {
          console.error(`Failed to refresh project ${localProject.name}:`, error);
          // Continue with other projects even if one fails
        }
      });

      await Promise.all(refreshPromises);
    } catch (error) {
      console.error('Failed to refresh shared projects:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleValue = (_event: SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center">
        <Tabs
          value={value}
          onChange={handleValue}
          sx={(theme) => ({
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 40,
            '& .MuiTabs-flexContainer': { gap: 0 },
            '& .MuiTab-root': {
              minHeight: 40,
              opacity: 1,
              textTransform: 'none',
              fontSize: theme.typography.tabText.fontSize,
              fontFamily: theme.typography.tabText.fontFamily,
              fontWeight: theme.typography.tabText.fontWeight,
              lineHeight: theme.typography.tabText.lineHeight,
              letterSpacing: theme.typography.tabText.letterSpacing,
              '&.Mui-selected': {
                fontWeight: theme.typography.tabText.fontWeight,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.vars?.palette?.accent?.main,
            },
          })}
          aria-label={t('aria-label.projectsPage.tabs', 'my-projects, examples, and rapid robots tabs')}
        >
          <Tab label={t('projectsPage.tabs.my-projects', 'My Projects')} value="my-projects" />
          <Tab label={t('projectsPage.tabs.rapid-robots', 'Rapid Robots')} value="rapid-robots" />

          {/*
            Temporarily hidden (BPS-373). Keep code around in case we re-enable.
            <Tab label={t('projectsPage.tabs.examples', 'Examples')} value="examples" />
          */}
        </Tabs>
      </Stack>
      {!isRapidRobotsPage && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={t('projectsPage.tooltip.refresh-shared-projects', 'Refresh all shared projects from the platform to get latest changes')}>
            <IconButton
              onClick={handleRefreshSharedProjects}
              disabled={isRefreshing}
              sx={(theme) => ({
                color: theme.vars?.palette?.primary?.main,
              })}
              aria-label={t('aria-label.refresh-shared-projects', 'refresh shared projects')}
            >
              {isRefreshing ? <CircularProgress size={24} /> : <SyncIcon />}
            </IconButton>
          </Tooltip>
          <Button variant="contained" onClick={handleOpenShareCode}>
            <Typography variant="buttonText">
              <Trans i18nKey="projectsPage.button.open-share-code">Open Share Code</Trans>
            </Typography>
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default ProjectsHeader;
